import React, { Fragment, useState } from 'react';
import PropTypes from 'prop-types';
import { graphql } from 'react-apollo';
import compose from 'lodash.flowright';
import Switch from 'react-switch';
import QRCode from 'qrcode.react';

import Button from '../../../../../../../../components/global/Button';
import { MeQuery } from '../../../../../../../../graphql/queries/account';
import useAlbumSlideshow from '../../../hooks/useAlbumSlideshow';
import getConfig from '../../../../../../../../util/config';
import { getSlideshowQRInsertUrl } from '../../helpers';

const propTypes = {
  account: PropTypes.shape({
    id: PropTypes.string,
    logo_image: PropTypes.shape({
      url: PropTypes.string,
    }),
  }),
  albumId: PropTypes.string,
  includeLogo: PropTypes.bool,
  reviewPhotos: PropTypes.bool,
  onUpdateQRCode: PropTypes.func.isRequired,
  onRequestClose: PropTypes.func.isRequired,
};
const QRCodeInsert = ({
  account,
  albumId,
  enabled,
  includeLogo,
  reviewPhotos,
  onUpdateQRCode,
  onRequestClose,
}) => {
  const [withLogo, setWithLogo] = useState(!!includeLogo);
  const [withReviewPhotos, setWithReviewPhotos] = useState(reviewPhotos);
  const [saving, setSaving] = useState(false);
  const { album } = useAlbumSlideshow({ albumId });
  const { app } = getConfig();
  const qrCodeUrl = getSlideshowQRInsertUrl(app?.baseUrl, album);

  const onDone = async () => {
    if (saving) {
      return;
    }

    setSaving(true);
    await onUpdateQRCode({
      withLogo,
      logo: withLogo ? account.logo_image : undefined,
      withCallout: true,
      enabled: !enabled,
      withReviewPhotos,
    });
    setSaving(false);
    onRequestClose();
  };

  return (
    <Fragment>
      <p className="text-dark text-sm text-center">
        QR Code will be added to the bottom right of slideshow{' '}
        <br className="d-none d-md-block" />
        and allow others to add their photos via text message!
      </p>
      <div className="d-flex flex-column flex-md-row align-items-start">
        <div className="w-100 d-flex flex-column justify-content-center align-items-center">
          <QRCode
            id="got-photos-qr"
            value={qrCodeUrl}
            renderAs="canvas"
            size={130}
            className="mb-1"
            imageSettings={
              withLogo
                ? {
                    src: account.logo_image.url,
                    height: 64,
                    width: 64,
                  }
                : undefined
            }
          />
          <div className="qr-code-insert-callout">SCAN TO ADD PHOTOS</div>
        </div>
        <div className="w-100">
          {/* In the meantime this will be removed until we're capable of ensuring this works as intended */}
          {false && account?.logo_image && (
            <div className="text-extra-bold text-dark d-flex w-100 justify-content-between mb-2">
              Include logo
              <Switch
                uncheckedIcon={false}
                checkedIcon={false}
                height={20}
                width={40}
                onColor="#7ace21"
                offColor="#cdd6e4"
                className="pl-2"
                onChange={() => setWithLogo(prev => !prev)}
                checked={withLogo}
              />
            </div>
          )}
          <div className="text-extra-bold text-dark d-flex w-100 align-items-center justify-content-between">
            Review photos before adding to slideshow
            <Switch
              uncheckedIcon={false}
              checkedIcon={false}
              height={20}
              width={40}
              onColor="#7ace21"
              offColor="#cdd6e4"
              className="pl-2"
              onChange={() => setWithReviewPhotos(prev => !prev)}
              checked={withReviewPhotos}
            />
          </div>
          <p className="text-xs text-light pr-3 mb-0">
            You will receive a text when there are photos to be reviewed.
          </p>
          <p className="text-light text-xs mt-2">
            Note: You may tap on the QR code to customize these settings
            anytime.
          </p>
        </div>
      </div>

      <div className="text-center mt-2">
        <Button
          onClick={onDone}
          btnStyle={enabled ? 'danger' : 'primary'}
          btnOutline={enabled}
          disabled={saving}
        >
          {saving ? (
            'Saving...'
          ) : (
            <Fragment>{enabled ? 'Remove QR' : 'Insert QR'}</Fragment>
          )}
        </Button>
      </div>
    </Fragment>
  );
};

const withMeQuery = graphql(MeQuery, {
  props: ({ data: { me } }) => ({
    account: me,
  }),
});

QRCodeInsert.propTypes = propTypes;

export default compose(withMeQuery)(QRCodeInsert);
